import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { callCrearServicioLogistica } from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PEDIR UN TRANSPORTE DE CARGA — el lado del cliente
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Se llega aquí desde «Enviar Paquete» → Logística, que es donde el fundador
 * dijo que conectaba: «estoy mirando dentro del perfil de pasajero donde vive
 * la opción de enviar paquete… ahí debemos agregar lo nuevo donde el usuario
 * vea el tipo de transporte que desea».
 *
 * ── POR QUÉ ESTO NO ES UN VIAJE ──────────────────────────────────────────
 *
 * Un motor o un carro se despachan al instante: el pasajero ofrece un precio y
 * el chofer más cercano acepta. Una mudanza no funciona así — hay que saber
 * cuántos muebles, qué piso, si hay ascensor. Por eso aquí no se ofrece un
 * precio y se espera: se pide, los transportistas COTIZAN, y el cliente elige.
 *
 * ── EL AVISO DE LA DIRECCIÓN NO ES DECORATIVO ───────────────────────────
 *
 * Los transportistas ven este trabajo en su escaparate, y el servidor les
 * manda solo el SECTOR y una coordenada redondeada a un kilómetro — nunca la
 * calle. Pero `descripcion` es texto libre, y nada impide teclear ahí la
 * dirección completa. Eso no se puede arreglar en el servidor: se arregla
 * diciéndolo aquí, donde la persona está escribiendo.
 */

type Tipo = 'CURIER' | 'MUDANZA' | 'CARGA' | 'GIRA';

const TIPOS: { id: Tipo; nombre: string; icono: any; ayuda: string }[] = [
  { id: 'MUDANZA', nombre: 'Mudanza', icono: 'home', ayuda: 'Muebles, electrodomésticos, una casa entera' },
  { id: 'CARGA', nombre: 'Carga y materiales', icono: 'construct', ayuda: 'Blocks, cemento, varilla, mercancía' },
  { id: 'CURIER', nombre: 'Curier grande', icono: 'cube', ayuda: 'Paquetería que no cabe en un motor' },
  { id: 'GIRA', nombre: 'Autobús para gira', icono: 'people', ayuda: 'Excursión, viaje de grupo' },
];

export default function LogisticaPedirScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [tipo, setTipo] = React.useState<Tipo | null>(null);
  const [descripcion, setDescripcion] = React.useState('');
  const [origen, setOrigen] = React.useState('');
  const [destino, setDestino] = React.useState('');
  const [cuando, setCuando] = React.useState('');
  const [monto, setMonto] = React.useState('');
  const [enviando, setEnviando] = React.useState(false);

  const avisar = (titulo: string, texto: string) => {
    // `Alert.alert` no pinta nada en react-native-web y el botón parecería
    // muerto. Esta pantalla también corre en el navegador.
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  /**
   * Cuándo, en milisegundos. Se aceptan «hoy», «mañana» o una fecha
   * dd/mm/aaaa, porque pedirle a alguien que teclee una fecha con formato es
   * cómo se consigue que abandone el formulario.
   */
  const cuandoEnMs = (): number | null => {
    const v = cuando.trim().toLowerCase();
    if (!v) return null;
    const dia = 24 * 60 * 60 * 1000;
    if (v === 'hoy') return Date.now();
    if (v === 'mañana' || v === 'manana') return Date.now() + dia;
    const m = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 9, 0, 0);
      const t = d.getTime();
      return Number.isFinite(t) ? t : null;
    }
    return null;
  };

  const loQueFalta = (): string | null => {
    if (!tipo) return 'Elige qué necesitas mover.';
    if (descripcion.trim().length < 10) {
      return 'Cuéntanos qué hay que mover, con un poco de detalle. Así te cotizan bien.';
    }
    if (!origen.trim()) return 'Dinos de dónde se recoge.';
    if (!destino.trim()) return 'Y a dónde se lleva.';
    if (!cuando.trim()) return 'Dinos para cuándo: «hoy», «mañana» o una fecha.';
    if (cuandoEnMs() === null) return 'No entendimos la fecha. Escribe «hoy», «mañana» o 25/10/2026.';
    if (monto.trim() && !/^\d{1,7}$/.test(monto.trim().replace(/[.,\s]/g, ''))) {
      return 'El monto va solo en números, sin puntos ni letras.';
    }
    return null;
  };

  const enviar = async () => {
    if (enviando) return;
    if (!uid) {
      avisar('Necesitas tu cuenta', 'Entra con tu cuenta para pedir un transporte.');
      return;
    }
    const falta = loQueFalta();
    if (falta) {
      avisar('Falta un dato', falta);
      return;
    }
    const fechaDeseadaMs = cuandoEnMs();
    if (fechaDeseadaMs === null) return;

    setEnviando(true);
    try {
      await callCrearServicioLogistica({
        tipo: tipo!,
        descripcion: descripcion.trim(),
        // Las coordenadas van en null: aquí se escribe la dirección a mano.
        // El servidor guarda el texto y, cuando no hay punto, el escaparate
        // manda solo el sector. No se inventa un punto a partir del texto:
        // una coordenada equivocada es peor que ninguna.
        origen: { texto: origen.trim(), lat: null, lng: null },
        destino: { texto: destino.trim(), lat: null, lng: null },
        fechaDeseadaMs,
        montoOfrecido: monto.trim() ? Number(monto.trim().replace(/[.,\s]/g, '')) : null,
      });
      avisar(
        'Tu solicitud quedó puesta',
        'Los transportistas que tengan el vehículo para esto te van a cotizar. Te avisamos en cuanto llegue la primera, y tú eliges.'
      );
      router.replace('/logistica-mis-pedidos' as any);
    } catch (e: any) {
      avisar('No pudimos enviarlo', mensajeDeError(e, 'Revisa tu conexión e inténtalo otra vez.'));
    } finally {
      setEnviando(false);
    }
  };

  if (!VERTICAL_LOGISTICA_VISIBLE) return null;

  const falta = loQueFalta();

  return (
    <ScreenBackground>
      <PantallaSegura>
        <View style={[estilos.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={estilos.salida}>
            <Ionicons name="chevron-back" size={22} color={colors.text} />
            <Text style={[estilos.salidaTexto, { color: colors.text }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={estilos.cuerpo} keyboardShouldPersistTaps="handled">
          <Text style={[estilos.titulo, { color: colors.text }]}>¿Qué necesitas mover?</Text>
          <Text style={[estilos.bajada, { color: colors.textSecondary }]}>
            Los transportistas te cotizan y tú eliges. No pagas nada por pedir.
          </Text>

          {TIPOS.map((x) => {
            const elegido = tipo === x.id;
            return (
              <TouchableOpacity
                key={x.id}
                onPress={() => setTipo(x.id)}
                style={[
                  estilos.opcion,
                  {
                    backgroundColor: colors.card,
                    borderColor: elegido ? '#EF6C00' : colors.border,
                    borderWidth: elegido ? 2 : 1,
                  },
                ]}
              >
                <Ionicons name={x.icono} size={24} color={elegido ? '#EF6C00' : colors.textSecondary} />
                <View style={estilos.opcionTexto}>
                  <Text style={[estilos.opcionTitulo, { color: colors.text }]}>{x.nombre}</Text>
                  <Text style={[estilos.opcionAyuda, { color: colors.textSecondary }]}>{x.ayuda}</Text>
                </View>
                {elegido && <Ionicons name="checkmark-circle" size={22} color="#EF6C00" />}
              </TouchableOpacity>
            );
          })}

          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>CUÉNTALES QUÉ ES</Text>
          <TextInput
            value={descripcion}
            onChangeText={setDescripcion}
            placeholder="Ej: una nevera, una cama y seis cajas. Tercer piso sin ascensor."
            placeholderTextColor={colors.textSecondary}
            multiline
            maxLength={500}
            style={[
              estilos.campo,
              estilos.campoLargo,
              { backgroundColor: colors.card, borderColor: colors.border, color: colors.text },
            ]}
          />
          {/* El aviso va AQUÍ, pegado al campo, no en una pantalla de ayuda que
              nadie abre. */}
          <View style={[estilos.aviso, { backgroundColor: colors.card, borderColor: '#D97706' }]}>
            <Ionicons name="shield-checkmark-outline" size={18} color="#D97706" />
            <Text style={[estilos.avisoTexto, { color: colors.textSecondary }]}>
              No pongas aquí tu dirección exacta. Los transportistas solo ven el sector hasta
              que tú elijas a uno; la dirección completa se la damos solo a ese.
            </Text>
          </View>

          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>DE DÓNDE Y A DÓNDE</Text>
          <TextInput
            value={origen}
            onChangeText={setOrigen}
            placeholder="Se recoge en… (calle, sector y ciudad)"
            placeholderTextColor={colors.textSecondary}
            maxLength={200}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <TextInput
            value={destino}
            onChangeText={setDestino}
            placeholder="Se lleva a… (calle, sector y ciudad)"
            placeholderTextColor={colors.textSecondary}
            maxLength={200}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />

          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>PARA CUÁNDO</Text>
          <TextInput
            value={cuando}
            onChangeText={setCuando}
            placeholder="«hoy», «mañana» o 25/10/2026"
            placeholderTextColor={colors.textSecondary}
            maxLength={20}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />

          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>
            CUÁNTO PIENSAS PAGAR (OPCIONAL)
          </Text>
          <TextInput
            value={monto}
            onChangeText={setMonto}
            placeholder="Ej: 8000. Si no sabes, déjalo vacío."
            placeholderTextColor={colors.textSecondary}
            keyboardType="number-pad"
            maxLength={9}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <Text style={[estilos.ayuda, { color: colors.textSecondary }]}>
            Poner un monto ayuda a que te coticen más rápido, pero el precio final lo acuerdan
            ustedes dos. Nosotros no retenemos tu dinero: le pagas directo al transportista.
          </Text>

          {falta && <Text style={[estilos.falta, { color: '#D97706' }]}>{falta}</Text>}
          <TouchableOpacity
            onPress={enviar}
            disabled={enviando}
            style={[estilos.enviar, { backgroundColor: falta ? colors.border : '#EF6C00' }]}
          >
            {enviando ? (
              <ActivityIndicator color="#FFF" />
            ) : (
              <Text style={[estilos.enviarTexto, { color: falta ? colors.textSecondary : '#FFF' }]}>
                Pedir cotizaciones
              </Text>
            )}
          </TouchableOpacity>
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  cabecera: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  salida: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4 },
  salidaTexto: { fontSize: 16, marginLeft: 2 },
  cuerpo: { paddingHorizontal: 16, paddingTop: 8 },
  titulo: { fontSize: 24, fontWeight: '700', marginBottom: 6 },
  bajada: { fontSize: 14, lineHeight: 20, marginBottom: 16 },
  rotulo: { fontSize: 12, fontWeight: '700', letterSpacing: 0.5, marginTop: 22, marginBottom: 8 },
  opcion: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, padding: 14, marginBottom: 8, gap: 12 },
  opcionTexto: { flex: 1 },
  opcionTitulo: { fontSize: 15, fontWeight: '600' },
  opcionAyuda: { fontSize: 12, marginTop: 2 },
  campo: { borderRadius: 12, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, marginBottom: 8 },
  campoLargo: { minHeight: 90, textAlignVertical: 'top' },
  aviso: { flexDirection: 'row', gap: 10, borderRadius: 12, borderWidth: 1, padding: 12, marginTop: 4 },
  avisoTexto: { flex: 1, fontSize: 13, lineHeight: 19 },
  ayuda: { fontSize: 13, lineHeight: 19, marginTop: 4 },
  falta: { fontSize: 13, marginTop: 18, marginBottom: 4, fontWeight: '600' },
  enviar: { borderRadius: 14, paddingVertical: 16, alignItems: 'center', marginTop: 10 },
  enviarTexto: { fontSize: 16, fontWeight: '700' },
});
